import { useEffect, useRef, useState } from "react";

import type { SuggestedChipItemProps } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterSuggestionsBar";
import useSetTimeout from "app/pages/sales-intelligence/hooks/useSetTimeout";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import useIabCategoriesById from "app/pages/sales-intelligence/modules/find-apps-filters/components/filters/IabCategoriesFilter/hooks/useIabCategoriesById";
import type IabCategoriesFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/IabCategoriesFilter/IabCategoriesFilter";
import type { IIabCategoryItemDescriptionDto } from "app/pages/sales-intelligence/modules/find-apps-filters/types/description.dto";

const MAX_SUGGESTIONS_COUNT = 4;

type Params = Pick<IFilterViewBaseProps<IabCategoriesFilter>, "filter" | "value"> & {
    handleSelect: (category?: IIabCategoryItemDescriptionDto) => void;
};

type Result = {
    isFilterDirty: boolean;
    isLoading: boolean;
    suggestedCategoryChips: readonly SuggestedChipItemProps[];
};

function useIabCategoriesFilter({ filter, value, handleSelect }: Params): Result {
    const timestampRef = useRef<number>(0);

    const [isLoading, setIsLoading] = useState(false);
    const [suggestedCategoryChips, setSuggestedCategoryChips] = useState<readonly SuggestedChipItemProps[]>([]);
    const timeout = useSetTimeout();

    const optionsById = useIabCategoriesById(filter.options);

    const clearCurrentCategoryChips = (): void => {
        if (!value?.values) {
            return;
        }

        const newSuggestedCategoryChips = suggestedCategoryChips.filter((c) => !value.values.includes(c.id));

        setSuggestedCategoryChips(newSuggestedCategoryChips);
    };

    const createNewSuggestedCategoryChips = (): readonly SuggestedChipItemProps[] => {
        if (!value?.values?.length) {
            return [];
        }

        const selectedOptions = value?.values.map((id) => optionsById[id]);

        const allSuggestions: readonly string[] = selectedOptions.flatMap((option) => option.suggestions || []);
        const uniqSuggestions = new Set<string>(allSuggestions);

        value?.values.forEach((id) => uniqSuggestions.delete(id));

        return Array.from(uniqSuggestions)
            .map(
                (id): SuggestedChipItemProps => ({
                    id,
                    text: optionsById[id].name,
                    onApply: () => handleSelect(optionsById[id]),
                }),
            )
            .reverse()
            .slice(0, MAX_SUGGESTIONS_COUNT);
    };

    useEffect(() => {
        clearTimeout(timestampRef.current);

        if (!value?.values?.length) {
            setIsLoading(false);

            return;
        }

        clearCurrentCategoryChips();

        const suggestions: readonly SuggestedChipItemProps[] = createNewSuggestedCategoryChips();

        setIsLoading(true);

        timestampRef.current = timeout(() => {
            setSuggestedCategoryChips(suggestions);
            setIsLoading(false);
        }, 1000);
    }, [optionsById, value?.values, handleSelect]);

    return {
        isFilterDirty: !!value?.values?.length,
        isLoading,
        suggestedCategoryChips,
    };
}

export default useIabCategoriesFilter;
